import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { FileSpreadsheet, Download } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { PageHeader } from '../components/common/PageHeader';
import { PlanGate } from '../components/common/planGate';
import { reportService } from '../services/index';
import { KEYS } from '../lib/queryKeys';
import { formatINR } from '../lib/format';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from 'recharts';

const DEPT_COLORS = ['#2E6DA4', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444'];

export default function Reports() {
  const now   = new Date();
  const month = now.getMonth() + 1;
  const year  = now.getFullYear();

  const { data: trendRaw, isLoading: trendLoading } = useQuery({
    queryKey: KEYS.trend(6),
    queryFn:  () => reportService.trend(6),
  });

  const { data: ytdRaw, isLoading: ytdLoading } = useQuery({
    queryKey: KEYS.ytd(year),
    queryFn:  () => reportService.ytd(year),
  });

  const { data: deptRaw, isLoading: deptLoading } = useQuery({
    queryKey: KEYS.departments(month, year),
    queryFn:  () => reportService.departments(month, year),
  });

  const trend = (trendRaw?.data || []).map((r) => ({ month: `${r.month}/${String(r.year).slice(2)}`, amount: r.totalNetPayroll }));
  const ytd   = ytdRaw?.data  || [];
  const depts = (deptRaw?.data || []).map((d, i) => ({ ...d, color: DEPT_COLORS[i % DEPT_COLORS.length] }));

  return (
    <div>
      <PageHeader title="Reports & Analytics" description="Visualize payroll trends and export financial data.">
        <PlanGate required="pro" fallback={
          <Button variant="outline" disabled className="opacity-50">
            <Download className="w-4 h-4 mr-1.5" /> Export (Pro)
          </Button>
        }>
          <Button variant="outline" onClick={() => toast.info('Export coming soon')}>
            <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
          <Button variant="outline" onClick={() => toast.info('Export coming soon')}>
            <Download className="w-4 h-4 mr-1.5" /> Export Excel
          </Button>
        </PlanGate>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card className="col-span-1 lg:col-span-2 p-5 sm:p-6 border-slate-200 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 font-heading mb-1">Monthly payroll expense</h3>
          <p className="text-xs text-slate-500 mb-5">Trailing 6 months</p>
          <div className="h-72">
            {trendLoading ? <div className="h-full flex items-center justify-center text-slate-400 text-sm">Loading…</div> :
             trend.length === 0 ? <div className="h-full flex items-center justify-center text-slate-400 text-sm">No payroll data yet</div> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#94A3B8" fontSize={12} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} tickLine={false} axisLine={false} />
                  <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13 }} cursor={{ fill: '#F1F5F9' }} />
                  <Bar dataKey="amount" fill="#2E6DA4" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card className="p-5 sm:p-6 border-slate-200 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 font-heading mb-1">Cost by department</h3>
          <p className="text-xs text-slate-500 mb-4">This month</p>
          <div className="h-56">
            {deptLoading ? <div className="h-full flex items-center justify-center text-slate-400 text-sm">Loading…</div> :
             depts.length === 0 ? <div className="h-full flex items-center justify-center text-slate-400 text-sm">No data</div> : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={depts} dataKey="total" nameKey="department" cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={2}>
                    {depts.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="space-y-1.5 mt-4">
            {depts.map((d) => (
              <div key={d.department} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: d.color }} />
                  <span className="text-slate-700">{d.department}</span>
                </div>
                <span className="font-mono font-medium text-slate-900">{formatINR(d.total)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* YTD table */}
      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="p-5 sm:p-6 border-b border-slate-200">
          <h3 className="text-base font-semibold text-slate-900 font-heading">Employee YTD summary ({year})</h3>
        </div>
        {ytdLoading ? <div className="p-8 text-center text-slate-400 text-sm">Loading…</div> :
         ytd.length === 0 ? <div className="p-8 text-center text-slate-400 text-sm">Run payroll to generate YTD data</div> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {['Employee', 'Department', 'YTD Gross', 'YTD PF', 'YTD ESI', 'YTD Net'].map((h) => (
                    <th key={h} className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {ytd.map((r) => (
                  <tr key={r.employeeId} className="hover:bg-slate-50">
                    <td className="px-6 py-3.5 text-sm font-medium text-slate-900">{r.employeeName}</td>
                    <td className="px-6 py-3.5 text-sm text-slate-600">{r.department}</td>
                    <td className="px-6 py-3.5 text-sm font-mono text-slate-900">{formatINR(r.ytdGross)}</td>
                    <td className="px-6 py-3.5 text-sm font-mono text-red-600">{formatINR(r.ytdPF)}</td>
                    <td className="px-6 py-3.5 text-sm font-mono text-red-600">{formatINR(r.ytdESI)}</td>
                    <td className="px-6 py-3.5 text-sm font-mono font-semibold text-[#2E6DA4]">{formatINR(r.ytdNet)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}